<template>
  <div ref="rootRef" class="btn-group dropdown" :class="{ show: isOpen }">
    <ButtonElement
      :class="stateDependantClass"
      class="btn--ms btn--neutral"
      id="dropdownMenu"
      :aria-expanded="isOpen"
      :is-disable="isDisable"
      @click="toggle"
    >

      <div v-if="type === 'button'" class="button-state">
        <LucideIcon :icon="icon"/>
        {{ name }}
      </div>

      <div v-if="type === 'buttonselect'" class="selectbox-state">
        <div>
          <LucideIcon :icon="selectedItem?.icon"/>
          <span class="ms-2">{{ selectedItem?.name }}</span>
        </div>
        <ChevronDown style="font-size: var(--fs-16);margin-left: 15px;" aria-hidden="true" />
      </div>

      <div v-if="type === 'buttonselectlarge'" class="selectbox-state">
        <div>
          <LucideIcon :icon="selectedItem?.icon"/>
          <span class="ms-2"><b>{{ selectedItem?.name }}</b></span>
          <div v-if="showContent" class="">
            <span class="dropdown-content">{{ selectedItem?.content }}</span>
          </div>
        </div>

        <ChevronDown style="font-size: var(--fs-16);margin-left: 15px;" aria-hidden="true" />
      </div>

    </ButtonElement>

    <ul class="dropdown-menu" :class="{ show: isOpen }" id="dropdownMenuList">
      <div class="dropdown-menu-box animation">
        <div class="dropdown-menu-head">{{ title }}</div>
        <li v-for="(option, index) in options" :key="index">
          <ul
            v-if="activeMenuItemIndex === index && option.subOptions?.options"
            :id="option.name"
            class="dropdown-menu show"
            style="left: 190px;top:0;"
          >
            <div class="dropdown-menu-head">{{ option.subOptions.title }}</div>
            <li v-for="(subOption, subIndex) in option.subOptions?.options" :key="subIndex">
              <div
                class="dropdown-item"
                @click="appendSelectedItemToInputGroup(subOption, index, Number(subIndex))"
              >
                <div class="icon">
                  <LucideIcon :icon="subOption.icon"/>
                </div>
                <div class="content">
                  <h6>{{ subOption.name }}</h6>
                  <p>{{ subOption.content }}</p>
                </div>
              </div>
            </li>
          </ul>

          <a
            class="dropdown-item "
            @click="setSelectedOption(index)"
            @mouseenter="hoverOnMenuItem(index)"
            :id="option.name"
          >
            <div class="icon">
              <LucideIcon :icon="option.icon"/>
            </div>
            <div class="content">
              <h6>{{ option.name }}</h6>
              <p>{{ option.content }}</p>
            </div>
            <div class="arrow">
              <ChevronRight v-if="option.subOptions" aria-hidden="true" />
            </div>
          </a>

        </li>
      </div>
    </ul>

  </div>
</template>

<script setup lang="ts">
import { ChevronDown, ChevronRight } from '@lucide/vue'
import {computed, ref} from 'vue';
import type {CommonNs, DropdownNs} from '@shared/types';
import {useDropdown} from '@shared/composables/use-dropdown';
import ButtonElement from '@shared/components/elements/button/button.element.vue';

const props = withDefaults(defineProps<{
  value?: string;
  title?: string;
  options: DropdownNs.DropdownOptionsItem[];
  type?: 'button' | 'buttonselect' | 'buttonselectlarge';
  name?: string;
  buttonClass?: string;
  icon?: string;
  iconType?: string;
  showContent?: boolean;
  isDisable?: boolean;
}>(), {
  type: 'buttonselect',
  name: '',
  buttonClass: 'btn-grey',
});

const emit = defineEmits<{
  (e: 'change', item: DropdownNs.DropdownOptionsItem): void;
  (e: 'update:value', value: string | undefined): void;
  (e: 'inputUpdate', group: CommonNs.InputFormGroup): void;
}>();

const {isOpen, rootRef, toggle, close} = useDropdown();

const selectedItem = ref<DropdownNs.DropdownOptionsItem | undefined>(
  props.options.find(item => item.value === props.value),
);
const activeMenuItemIndex = ref(-1);

const stateDependantClass = computed(() => ({
  'btn-grey':                                  props.type === 'button',
  'btn-dropdown':                              props.type === 'buttonselect',
  'btn-dropdown btn-select btn-dropdown-large': props.type === 'buttonselectlarge',
}));

function setSelectedOption(key: number): void {
  const option = props.options[key];
  if (!option) return;
  selectedItem.value = option;
  emit('update:value', option.value);
  emit('change', option);
  close();
}

function hoverOnMenuItem(activeIndex: number): void {
  activeMenuItemIndex.value = activeIndex;
}

function appendSelectedItemToInputGroup(
  item: DropdownNs.DropdownOptionsItem,
  index: number,
  subIndex: number,
): void {
  const inputGroup: CommonNs.InputFormGroup = {
    label:    item.name,
    icon:     item.icon as string,
    iconType: item.iconType as string,
    input: {
      name:        item.inputName as string,
      type:        item.inputType as never,
      placeholder: item.name,
    },
  };
  props.options[index]?.subOptions?.options.splice(subIndex, 1);
  emit('inputUpdate', inputGroup);
}
</script>
<style lang="scss" scoped>

.btn-group {
  width: 100%;
}

.dropdown-menu {
  min-width: 300px;
}

.btn-dropdown {
  width: 100%;
  text-align: left;
  padding: 0 15px;

  & .selectbox-state {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }
}

.dropdown-content {
  font-size: var(--fs-12);
  color: var(--text-neutral);
  width: 90%;
  display: flex;
  margin-top: 5px;
}

.dropdown-menu {
  & .dropdown-item {
    padding: 0.5rem 1rem;
    align-items: flex-start;

    & > .icon {
      width: 30px;
      margin-right: 5px;
      color: var(--text-dark);
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: var(--fs-14);
      margin-top: 3px;
    }

    & > .content {
      width: 100%;
      white-space: initial;
      margin: 0 5px;
      display: flex;
      justify-content: flex-start;
      align-items: flex-start;
      flex-direction: column;

      & > h6 {
        font-size: var(--fs-14);
        font-weight: 600;
        margin: var(--space-0);
        margin-bottom: 5px;
      }

      & > p {
        margin-bottom: 0;
        font-size: var(--fs-12);
        color: var(--text-neutral);
      }
    }

    & > .arrow {
      width: 15px;
    }
  }
}

</style>
